import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  Siren,
  Shield,
  Phone,
  MapPin,
  Clock,
  Users,
  X,
  Volume2,
  VolumeX,
  Settings,
  Bell,
  Send,
  Eye,
  CheckCircle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/shared/utils';

interface EmergencyAlert {
  id: string;
  title: string;
  message: string;
  type: 'fire' | 'medical' | 'security' | 'weather' | 'utility' | 'evacuation' | 'other';
  severity: 'info' | 'warning' | 'critical' | 'emergency';
  status: 'active' | 'resolved' | 'expired';
  issuedBy: string;
  issuerInfo: {
    name: string;
    role: string;
    contact?: string;
  };
  timestamp: string;
  expiresAt?: string;
  affectedAreas: string[];
  instructions: string[];
  emergencyContacts: EmergencyContact[];
  acknowledgments: string[];
  location?: {
    building?: string;
    floor?: string;
    unit?: string;
    description?: string;
  };
  isAudioEnabled: boolean;
  isSystemWide: boolean;
  estimatedDuration?: string;
}

interface EmergencyContact {
  id: string;
  name: string;
  role: string;
  phone: string;
  email?: string;
  isPrimary: boolean;
}

interface AlertSettings {
  enableSound: boolean;
  enablePushNotifications: boolean;
  enableEmailAlerts: boolean;
  enableSMSAlerts: boolean;
  quietHours: {
    enabled: boolean;
    start: string;
    end: string;
  };
}

// Mock data
const mockEmergencyContacts: EmergencyContact[] = [
  {
    id: '1',
    name: 'Emergency Services',
    role: 'Emergency',
    phone: '911',
    isPrimary: true,
  },
  {
    id: '2',
    name: 'Security Desk',
    role: 'Security',
    phone: '(555) 123-0001',
    email: 'security@community.com',
    isPrimary: true,
  },
  {
    id: '3',
    name: 'Maintenance Emergency',
    role: 'Maintenance',
    phone: '(555) 123-0002',
    email: 'maintenance@community.com',
    isPrimary: false,
  },
  {
    id: '4',
    name: 'Management Office',
    role: 'Management',
    phone: '(555) 123-0003',
    email: 'office@community.com',
    isPrimary: false,
  },
];

const mockAlerts: EmergencyAlert[] = [
  {
    id: '1',
    title: 'Building Evacuation - Fire Alarm',
    message:
      'Fire alarm has been triggered in Building A. All residents must evacuate immediately via the nearest stairwell. Do not use elevators. Proceed to the designated assembly area in the parking lot.',
    type: 'fire',
    severity: 'emergency',
    status: 'active',
    issuedBy: 'security1',
    issuerInfo: {
      name: 'Security Command',
      role: 'Security Team',
      contact: '(555) 123-0001',
    },
    timestamp: '2024-01-24T14:30:00Z',
    affectedAreas: ['Building A', 'All Floors'],
    instructions: [
      'Exit via nearest stairwell immediately',
      'Do not use elevators',
      'Proceed to parking lot assembly area',
      'Remain calm and assist others if needed',
      'Wait for all-clear from fire department',
    ],
    emergencyContacts: mockEmergencyContacts.filter((c) => c.isPrimary),
    acknowledgments: ['user1', 'user2'],
    location: {
      building: 'Building A',
      description: 'Fire detected on 3rd floor',
    },
    isAudioEnabled: true,
    isSystemWide: true,
  },
  {
    id: '2',
    title: 'Severe Weather Warning',
    message:
      'The National Weather Service has issued a severe thunderstorm warning for our area. Heavy rain, strong winds, and possible hail expected until 8:00 PM. Secure outdoor furniture and avoid unnecessary travel.',
    type: 'weather',
    severity: 'warning',
    status: 'active',
    issuedBy: 'management1',
    issuerInfo: {
      name: 'Community Management',
      role: 'Emergency Coordinator',
      contact: '(555) 123-0003',
    },
    timestamp: '2024-01-24T16:00:00Z',
    expiresAt: '2024-01-24T20:00:00Z',
    affectedAreas: ['All Buildings', 'Outdoor Areas'],
    instructions: [
      'Secure outdoor furniture and decorations',
      'Close all windows and doors',
      'Avoid unnecessary travel',
      'Stay away from windows during the storm',
      'Report any damage to management',
    ],
    emergencyContacts: mockEmergencyContacts,
    acknowledgments: ['user1', 'user3', 'user4'],
    isAudioEnabled: false,
    isSystemWide: true,
    estimatedDuration: 'Until 8:00 PM',
  },
  {
    id: '3',
    title: 'Water Service Interruption',
    message:
      'Due to an emergency water main repair, water service will be interrupted to Buildings B and C from 10:00 PM tonight until 6:00 AM tomorrow. Bottled water will be available at the community center.',
    type: 'utility',
    severity: 'warning',
    status: 'active',
    issuedBy: 'maintenance1',
    issuerInfo: {
      name: 'Maintenance Team',
      role: 'Facilities Manager',
      contact: '(555) 123-0002',
    },
    timestamp: '2024-01-24T18:00:00Z',
    expiresAt: '2024-01-25T06:00:00Z',
    affectedAreas: ['Building B', 'Building C'],
    instructions: [
      'Fill containers with water before 10:00 PM',
      'Bottled water available at community center',
      'Service expected to resume by 6:00 AM',
      'Report any issues after service restoration',
    ],
    emergencyContacts: mockEmergencyContacts.filter(
      (c) => c.role === 'Maintenance' || c.role === 'Management',
    ),
    acknowledgments: ['user2', 'user5'],
    location: {
      description: 'Water main break on Oak Street',
    },
    isAudioEnabled: false,
    isSystemWide: false,
    estimatedDuration: '8 hours',
  },
  {
    id: '4',
    title: 'Medical Emergency - Building D',
    message:
      'Emergency medical services are responding to an incident in Building D. Please keep hallways and elevator access clear. Residents of Building D should use alternate exits if possible.',
    type: 'medical',
    severity: 'critical',
    status: 'resolved',
    issuedBy: 'security2',
    issuerInfo: {
      name: 'Security Team',
      role: 'Security Officer',
      contact: '(555) 123-0001',
    },
    timestamp: '2024-01-24T12:15:00Z',
    expiresAt: '2024-01-24T13:00:00Z',
    affectedAreas: ['Building D'],
    instructions: [
      'Keep hallways clear for emergency personnel',
      'Use alternate exits if possible',
      'Do not interfere with emergency response',
      'Situation is under control',
    ],
    emergencyContacts: mockEmergencyContacts.filter((c) => c.isPrimary),
    acknowledgments: ['user1', 'user2', 'user3', 'user4', 'user5'],
    location: {
      building: 'Building D',
      floor: '2nd Floor',
    },
    isAudioEnabled: false,
    isSystemWide: false,
  },
];

const alertTypeIcons = {
  fire: Siren,
  medical: Shield,
  security: Shield,
  weather: AlertTriangle,
  utility: Settings,
  evacuation: Users,
  other: AlertTriangle,
};

const severityColors = {
  info: 'bg-blue-100 text-blue-800 border-blue-300',
  warning: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  critical: 'bg-orange-100 text-orange-800 border-orange-300',
  emergency: 'bg-red-100 text-red-800 border-red-300',
};

interface EmergencyAlertsProps {
  className?: string;
  currentUserId?: string;
  isAdmin?: boolean;
}

export function EmergencyAlerts({
  className,
  currentUserId = 'user1',
  isAdmin = false,
}: EmergencyAlertsProps) {
  const [selectedTab, setSelectedTab] = useState('active');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [activeAlert, setActiveAlert] = useState<EmergencyAlert | null>(null);

  const [alertSettings, setAlertSettings] = useState<AlertSettings>({
    enableSound: true,
    enablePushNotifications: true,
    enableEmailAlerts: true,
    enableSMSAlerts: false,
    quietHours: {
      enabled: false,
      start: '22:00',
      end: '07:00',
    },
  });

  // Form state for creating alerts
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'other' as EmergencyAlert['type'],
    severity: 'warning' as EmergencyAlert['severity'],
    affectedAreas: '',
    instructions: '',
    location: '',
    isSystemWide: true,
    isAudioEnabled: false,
    expiresIn: '1',
  });

  // Check for active emergency alerts on component mount
  useEffect(() => {
    const emergencyAlerts = mockAlerts.filter(
      (alert) => alert.status === 'active' && alert.severity === 'emergency',
    );

    if (emergencyAlerts.length > 0) {
      setActiveAlert(emergencyAlerts[0]);
    }
  }, []);

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getTimeRemaining = (expiresAt?: string) => {
    if (!expiresAt) return null;

    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry.getTime() - now.getTime();

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    } else {
      return `${minutes}m remaining`;
    }
  };

  const filteredAlerts = mockAlerts.filter((alert) => {
    if (selectedTab === 'active') return alert.status === 'active';
    if (selectedTab === 'resolved') return alert.status === 'resolved';
    if (selectedTab === 'all') return true;
    return true;
  });

  const handleAcknowledgeAlert = (alertId: string) => {
    // In a real implementation, this would acknowledge the alert via API
    console.log('Acknowledging alert:', alertId);
  };

  const handleCreateAlert = () => {
    // In a real implementation, this would create the alert via API
    console.log('Creating emergency alert:', formData);
    setIsCreateDialogOpen(false);
    setFormData({
      title: '',
      message: '',
      type: 'other',
      severity: 'warning',
      affectedAreas: '',
      instructions: '',
      location: '',
      isSystemWide: true,
      isAudioEnabled: false,
      expiresIn: '1',
    });
  };

  const dismissActiveAlert = () => {
    setActiveAlert(null);
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Active Emergency Alert Banner */}
      {activeAlert && (
        <Alert className="border-red-500 bg-red-50 animate-pulse">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertTitle className="text-red-800 font-bold flex items-center justify-between">
            {activeAlert.title}
            <Button
              variant="ghost"
              size="sm"
              onClick={dismissActiveAlert}
              className="text-red-600 hover:text-red-800"
            >
              <X className="w-4 h-4" />
            </Button>
          </AlertTitle>
          <AlertDescription className="text-red-700">{activeAlert.message}</AlertDescription>
          <div className="mt-3 flex items-center gap-2">
            <Button
              size="sm"
              variant="default"
              onClick={() => handleAcknowledgeAlert(activeAlert.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Acknowledge
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-100"
            >
              View Details
            </Button>
          </div>
        </Alert>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Emergency Alerts</h2>
          <p className="text-muted-foreground">
            Community safety notifications and emergency communications
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAudioEnabled(!audioEnabled)}
            className={cn('gap-2', audioEnabled ? 'text-green-600' : 'text-muted-foreground')}
          >
            {audioEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            Audio {audioEnabled ? 'On' : 'Off'}
          </Button>

          <Dialog open={isSettingsDialogOpen} onOpenChange={setIsSettingsDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Alert Settings</DialogTitle>
                <DialogDescription>
                  Configure how you receive emergency notifications
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="sound-alerts">Sound Alerts</Label>
                  <Switch
                    id="sound-alerts"
                    checked={alertSettings.enableSound}
                    onCheckedChange={(checked) =>
                      setAlertSettings({ ...alertSettings, enableSound: checked })
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="push-notifications">Push Notifications</Label>
                  <Switch
                    id="push-notifications"
                    checked={alertSettings.enablePushNotifications}
                    onCheckedChange={(checked) =>
                      setAlertSettings({ ...alertSettings, enablePushNotifications: checked })
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="email-alerts">Email Alerts</Label>
                  <Switch
                    id="email-alerts"
                    checked={alertSettings.enableEmailAlerts}
                    onCheckedChange={(checked) =>
                      setAlertSettings({ ...alertSettings, enableEmailAlerts: checked })
                    }
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="sms-alerts">SMS Alerts</Label>
                  <Switch
                    id="sms-alerts"
                    checked={alertSettings.enableSMSAlerts}
                    onCheckedChange={(checked) =>
                      setAlertSettings({ ...alertSettings, enableSMSAlerts: checked })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="quiet-hours">Quiet Hours</Label>
                    <Switch
                      id="quiet-hours"
                      checked={alertSettings.quietHours.enabled}
                      onCheckedChange={(checked) =>
                        setAlertSettings({
                          ...alertSettings,
                          quietHours: { ...alertSettings.quietHours, enabled: checked },
                        })
                      }
                    />
                  </div>

                  {alertSettings.quietHours.enabled && (
                    <div className="flex items-center gap-2">
                      <Input
                        type="time"
                        value={alertSettings.quietHours.start}
                        onChange={(e) =>
                          setAlertSettings({
                            ...alertSettings,
                            quietHours: { ...alertSettings.quietHours, start: e.target.value },
                          })
                        }
                        className="w-24"
                      />
                      <span className="text-sm text-muted-foreground">to</span>
                      <Input
                        type="time"
                        value={alertSettings.quietHours.end}
                        onChange={(e) =>
                          setAlertSettings({
                            ...alertSettings,
                            quietHours: { ...alertSettings.quietHours, end: e.target.value },
                          })
                        }
                        className="w-24"
                      />
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button onClick={() => setIsSettingsDialogOpen(false)}>Save Settings</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {isAdmin && (
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Bell className="w-4 h-4 mr-2" />
                  Send Alert
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Send Emergency Alert</DialogTitle>
                  <DialogDescription>
                    Send an emergency notification to community members
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="alert-type">Alert Type</Label>
                      <Select
                        value={formData.type}
                        onValueChange={(value: EmergencyAlert['type']) =>
                          setFormData({ ...formData, type: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fire">Fire</SelectItem>
                          <SelectItem value="medical">Medical</SelectItem>
                          <SelectItem value="security">Security</SelectItem>
                          <SelectItem value="weather">Weather</SelectItem>
                          <SelectItem value="utility">Utility</SelectItem>
                          <SelectItem value="evacuation">Evacuation</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="severity">Severity</Label>
                      <Select
                        value={formData.severity}
                        onValueChange={(value: EmergencyAlert['severity']) =>
                          setFormData({ ...formData, severity: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="info">Info</SelectItem>
                          <SelectItem value="warning">Warning</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                          <SelectItem value="emergency">Emergency</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="alert-title">Alert Title</Label>
                    <Input
                      id="alert-title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Enter alert title"
                    />
                  </div>

                  <div>
                    <Label htmlFor="alert-message">Message</Label>
                    <Textarea
                      id="alert-message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Enter detailed alert message"
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label htmlFor="affected-areas">Affected Areas</Label>
                    <Input
                      id="affected-areas"
                      value={formData.affectedAreas}
                      onChange={(e) => setFormData({ ...formData, affectedAreas: e.target.value })}
                      placeholder="e.g., Building A, All Floors"
                    />
                  </div>

                  <div>
                    <Label htmlFor="instructions">Instructions (one per line)</Label>
                    <Textarea
                      id="instructions"
                      value={formData.instructions}
                      onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                      placeholder="Enter safety instructions, one per line"
                      rows={3}
                    />
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="system-wide"
                        checked={formData.isSystemWide}
                        onCheckedChange={(checked) =>
                          setFormData({ ...formData, isSystemWide: checked })
                        }
                      />
                      <Label htmlFor="system-wide">System-wide alert</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="audio-enabled"
                        checked={formData.isAudioEnabled}
                        onCheckedChange={(checked) =>
                          setFormData({ ...formData, isAudioEnabled: checked })
                        }
                      />
                      <Label htmlFor="audio-enabled">Enable audio alert</Label>
                    </div>
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateAlert} className="bg-red-600 hover:bg-red-700">
                    <Send className="w-4 h-4 mr-2" />
                    Send Alert
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Emergency Contacts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5" />
            Emergency Contacts
          </CardTitle>
          <CardDescription>Important contact information for emergencies</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {mockEmergencyContacts.map((contact) => (
              <div key={contact.id} className="text-center p-3 border rounded-lg">
                <div className="font-semibold">{contact.name}</div>
                <div className="text-sm text-muted-foreground mb-2">{contact.role}</div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => window.open(`tel:${contact.phone}`)}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  {contact.phone}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Alerts List */}
      <Card>
        <CardHeader>
          <CardTitle>Alert History</CardTitle>
          <Tabs value={selectedTab} onValueChange={setSelectedTab}>
            <TabsList>
              <TabsTrigger value="active">
                Active ({mockAlerts.filter((a) => a.status === 'active').length})
              </TabsTrigger>
              <TabsTrigger value="resolved">Resolved</TabsTrigger>
              <TabsTrigger value="all">All Alerts</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            <div className="space-y-4">
              {filteredAlerts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Shield className="w-12 h-12 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No alerts found</h3>
                  <p>No emergency alerts match your current filter</p>
                </div>
              ) : (
                filteredAlerts.map((alert) => {
                  const IconComponent = alertTypeIcons[alert.type];
                  const hasAcknowledged = alert.acknowledgments.includes(currentUserId);

                  return (
                    <Card
                      key={alert.id}
                      className={cn(
                        'border-l-4',
                        severityColors[alert.severity],
                        hasAcknowledged && 'opacity-75',
                      )}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <div
                              className={cn(
                                'p-2 rounded-lg',
                                alert.severity === 'emergency'
                                  ? 'bg-red-500 text-white'
                                  : alert.severity === 'critical'
                                    ? 'bg-orange-500 text-white'
                                    : alert.severity === 'warning'
                                      ? 'bg-yellow-500 text-white'
                                      : 'bg-blue-500 text-white',
                              )}
                            >
                              <IconComponent className="w-5 h-5" />
                            </div>

                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <CardTitle className="text-lg">{alert.title}</CardTitle>
                                <Badge className={cn('text-xs', severityColors[alert.severity])}>
                                  {alert.severity}
                                </Badge>
                                {alert.status === 'active' && (
                                  <Badge variant="default" className="text-xs">
                                    Active
                                  </Badge>
                                )}
                              </div>

                              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                                <div className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {formatTimestamp(alert.timestamp)}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Users className="w-4 h-4" />
                                  {alert.acknowledgments.length} acknowledged
                                </div>
                                {alert.location && (
                                  <div className="flex items-center gap-1">
                                    <MapPin className="w-4 h-4" />
                                    {alert.location.building || alert.location.description}
                                  </div>
                                )}
                              </div>

                              {alert.expiresAt && alert.status === 'active' && (
                                <div className="mb-2">
                                  <div className="text-sm text-muted-foreground">
                                    {getTimeRemaining(alert.expiresAt)}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {alert.isAudioEnabled && (
                              <Volume2 className="w-4 h-4 text-muted-foreground" />
                            )}
                            {hasAcknowledged && <CheckCircle className="w-4 h-4 text-green-600" />}
                          </div>
                        </div>
                      </CardHeader>

                      <CardContent>
                        <p className="text-muted-foreground mb-4">{alert.message}</p>

                        {alert.affectedAreas.length > 0 && (
                          <div className="mb-3">
                            <h4 className="text-sm font-semibold mb-1">Affected Areas:</h4>
                            <div className="flex flex-wrap gap-1">
                              {alert.affectedAreas.map((area, index) => (
                                <Badge key={index} variant="outline" className="text-xs">
                                  {area}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {alert.instructions.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold mb-2">Instructions:</h4>
                            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                              {alert.instructions.map((instruction, index) => (
                                <li key={index}>{instruction}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <Avatar className="w-6 h-6">
                                <AvatarFallback className="text-xs">
                                  {alert.issuerInfo.name
                                    .split(' ')
                                    .map((n) => n[0])
                                    .join('')}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="text-sm font-medium">{alert.issuerInfo.name}</div>
                                <div className="text-xs text-muted-foreground">
                                  {alert.issuerInfo.role}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            {alert.issuerInfo.contact && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(`tel:${alert.issuerInfo.contact}`)}
                              >
                                <Phone className="w-4 h-4 mr-2" />
                                Contact
                              </Button>
                            )}

                            {!hasAcknowledged && alert.status === 'active' && (
                              <Button size="sm" onClick={() => handleAcknowledgeAlert(alert.id)}>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Acknowledge
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
